<script setup>
import { Power4, gsap } from 'gsap'

defineProps({ blok: Object })

const { t, locale } = useI18n()
const { path } = useRoute()
const localePath = useLocalePath()
const activeIndex = ref(null)
const target = ref(null)
const targetIsVisible = ref(false)
const { width } = useWindowSize()

const { fetchData, page, perPage, formattedData, allStories, total, loading } = useActivities()
await fetchData()

const { stop } = useIntersectionObserver(
  target,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting)
      targetIsVisible.value = isIntersecting
  },
)

const filteredCount = computed(() => {
  return Math.ceil(total.value / perPage.value)
})

const filteredPerPage = computed(() => {
  return formattedData.value.slice(0, perPage.value)
})

async function scrollTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function addParams() {
  await navigateTo(localePath(
    {
      path,
      query: {
        page: page.value,
      },
    },
  ))
}

async function previous() {
  page.value--
  if (page.value >= 1) {
    await Promise.all([
      addParams(),
      fetchData(),
      scrollTop(),
    ])
  }
  else {
    page.value = 1
  }
}
async function next() {
  page.value++
  if (page.value <= filteredCount.value) {
    await Promise.all([
      addParams(),
      fetchData(),
      scrollTop(),
    ])
  }
  else {
    page.value = filteredCount.value
  }
}

function onEnter(el, done) {
  gsap.to(el, 0.1, {
    autoAlpha: 1,
    ease: Power4.easeIn,
    onComplete: done,
  }, {
    autoAlpha: 0,
    ease: Power4.easeIn,
    onComplete: done,
  })
  done()
}
function onLeave(el, done) {
  gsap.to(el, 0.1, {
    autoAlpha: 0,
    ease: Power4.easeIn,
    onComplete: done,
  }, {
    autoAlpha: 1,
    ease: Power4.easeIn,
    onComplete: done,
  })
  done()
}

function onBeforeEnter(el) {
  el.style.opacity = 0
}

function onHover(index) {
  activeIndex.value = index
}

const itemsToShow = ref(4)
function onResize() {
  if (width.value > 1578)
    itemsToShow.value = 4
  else if (width.value > 1024 && width.value < 1578)
    itemsToShow.value = 3
  else if (width.value > 576 && width.value < 1024)
    itemsToShow.value = 2
  else if (width.value > 0 && width.value < 576)
    itemsToShow.value = 1
}

onMounted(() => {
  onResize()
  window.addEventListener('resize', onResize)
})
onUnmounted(() => window.removeEventListener('resize', onResize))
</script>

<template>
  <div v-if="filteredPerPage?.length || allStories?.length" ref="target" v-editable="blok" :class="blok.carousel ? 'border-b border-slate-200' : 'pt-mobile-navbar lg:pt-desktop-navbar'">
    <div class="lg:py-desktop' : 'pb-mobile lg:pt-12' py-mobile container container lg:pb-desktop">
      <div
        v-if="blok.header"
        class="flex items-center text-sm text-green-500 font-semibold uppercase md:text-lg sm:text-md"
      >
        {{ blok.header }}
      </div>
      <h2 v-if="blok.title" class="mx-auto mb-custom-lg text-3xl font-bold md:text-4xl">
        {{ blok.title }}
      </h2>
      <p v-if="blok.description" class="mb-custom-lg">
        {{ blok.description }}
      </p>
      <ul v-if="Array.isArray(blok.actions)" class="mb-8 mt-6 flex flex-wrap gap-4 gap-y-custom-md p-0" :class="[{ 'justify-center': blok.center }]">
        <li v-for="action in blok?.actions" :key="action.uuid" class="mb-0">
          <StoryblokComponent :blok="action" :theme="blok.type" />
        </li>
      </ul>
      <div v-if="blok.carousel" class="relative">
        <CarouselNewsArticleComponent
          class="references-carousel transition-[transform,opacity] duration-700 delay-500 ease-in-out [&>div>ol]:!mb-0 [&>div]:!py-0"
          :categories="[]"
          :items-to-show="itemsToShow"
          :breakpoints="{
            0: {
              itemsToShow: 1,
            },
            576: {
              itemsToShow: 2,
            },
            1024: {
              itemsToShow: 3,
            },
            1578: {
              itemsToShow: 4,
            },
            2160: {
              itemsToShow: 4,
            },
          }"
          :is-visible="targetIsVisible"
          :articles="allStories"
        />
      </div>
      <div v-else>
        <TransitionGroup
          :css="false"
          name="list"
          tag="div"
          class="grid gap-custom-lg 2xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2"
          @enter="onEnter"
          @leave="onLeave"
          @before-enter="onBeforeEnter"
        >
          <CardArticleCard
            v-for="(item, index) in filteredPerPage"
            :key="item"
            class="transition-all duration-300"
            :article="item.content"
            :slug="item.full_slug"
            :active-index="activeIndex"
            :index="index"
            :loading="loading"
            :categories="[]"
            @mouseover="onHover(index)"
            @mouseleave="activeIndex = null"
          />
        </TransitionGroup>
        <UINewPagination v-if="filteredCount >= 1" :page="Number(page)" :count="Number(filteredCount)" @previous="previous" @next="next" />
      </div>
    </div>
  </div>
</template>

<style>
.references-carousel .bg-red-gradient {
  @apply !bg-green-500 !bg-none;
}
.references-carousel  button.carousel__next div.flex, .references-carousel button.carousel__prev div.flex {
  @apply !bg-green-500 !ring-green-500/20;
}

.references-carousel button.carousel__prev div.flex svg {
  @apply !text-white;
}

.references-carousel button.carousel__prev {
  @apply !-left-5;
}

.references-carousel button.carousel__next {
  @apply !-right-5;
}
</style>
